import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';

export default function Bookings(){
  const { user } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const preEvent = params.get('event');

  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ eventId: preEvent || '', seats: 1 });
  const [searchId, setSearchId] = useState('');
  const [availability, setAvailability] = useState(null);
  const [usersList, setUsersList] = useState([]);

  useEffect(()=>{
    fetchEvents();
    fetchBookings();
    if (preEvent) fetchAvailability(preEvent);
    // populate manager users if applicable
    if (user && (user.role === 'Admin' || user.role === 'Manager')){
      api.get('/users/list').then(r=>{
        setUsersList(r.data || []);
      }).catch(()=>{});
    }
  },[]);

  async function fetchEvents(){
    try{
      const r = await api.get('/events');
      setEvents(r.data || []);
    }catch(e){ }
  }

  async function fetchAvailability(eventId){
    try{
      const r = await api.get(`/bookings/event/${eventId}`);
      setAvailability(r.data || null);
    }catch(e){ setAvailability(null); }
  }

  async function fetchBookings(){
    setLoading(true);
    setError(null);
    try{
      const r = await api.get('/bookings' + (preEvent ? `?eventId=${preEvent}` : ''));
      setBookings(Array.isArray(r.data) ? r.data : [r.data]);
    }catch(e){ setError(e.userMessage || e.message || 'Could not load bookings.'); setBookings([]); }
    finally{ setLoading(false); }
  }

  async function handleCreate(e){
    e.preventDefault();
    setError(null);
    try{
      const seats = Number(form.seats);
      const amount = getTicketPrice(form.eventId) * seats;
      const payload = { eventId: form.eventId, seats, amount };
      const r = await api.post('/bookings', payload);
      const created = r.data;
      setBookings(prev => [created, ...prev]);
      setForm({ eventId: '', seats: 1 });
      setAvailability(null);
    }catch(e){ setError(e.userMessage || e.message || 'Create booking failed'); }
  }

  async function updatePayment(id, status){
    setError(null);
    try{
      const r = await api.put(`/bookings/${id}/payment`, { paymentStatus: status });
      const updated = r.data;
      setBookings(prev => prev.map(b => (b.id === updated.id ? updated : b)));
    }catch(e){
      setError(e.userMessage || e.message || 'Update failed');
    }
  }

  const canManagePayments = user && ['Admin','Manager','Sales Clerk'].includes(user.role);

  const filtered = searchId ? bookings.filter(b => String(b.id) === String(searchId)) : bookings;

  function getEventName(id){
    const ev = events.find(x=>String(x.id) === String(id));
    return ev ? ev.name : id;
  }

  function paymentPillClass(status){
    if(!status) return 'status-pill status-pending';
    const s = String(status).toLowerCase();
    if(s.includes('completed')) return 'status-pill status-completed';
    if(s.includes('refun')) return 'status-pill status-refunded';
    if(s.includes('cancel')) return 'status-pill status-cancelled';
    return 'status-pill status-pending';
  }

  function getTicketPrice(eventId){
    const ev = events.find(x=>String(x.id) === String(eventId));
    if(!ev) return 0;
    return Number(ev.ticket_price ?? ev.ticketPrice ?? ev.price ?? 0);
  }
  const currentTicketPrice = getTicketPrice(form.eventId);
  const currentTotal = currentTicketPrice * Number(form.seats || 0);

  return (
    <div className="p-6">
      <header className="bookings-hero p-6 rounded-lg mb-6">
        <div>
          <h2 className="text-2xl hero-title">Bookings</h2>
          <p className="hero-sub">Create and manage reservations. Quick actions and payment controls for staff.</p>
        </div>
        <div className="hero-cta">Signed in: <strong>{user?.name || user?.email}</strong> <span className="muted">({user?.role})</span></div>
      </header>

      {error && <div className="mb-4 text-red-600">{error}</div>}

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        {/* Create booking */}
        {user && user.role === 'Student' && (
          <form onSubmit={handleCreate} className="create-booking p-4 bg-white rounded shadow space-y-3">
            <h3 className="font-semibold">Create Booking</h3>
            <div className="grid grid-cols-1 gap-3">
              <select className="border p-2 rounded" value={form.eventId} onChange={e=>setForm(f=>({...f,eventId:e.target.value}))} required>
                <option value="">Select event</option>
                {events.map(ev => (
                  <option key={ev.id} value={ev.id}>{ev.name} ({ev.date})</option>
                ))}
              </select>

              <div className="grid grid-cols-2 gap-2">
                <input className="border p-2 rounded" type="number" min="1" value={form.seats} onChange={e=>setForm(f=>({...f,seats:e.target.value}))} />
                <div className="p-2 rounded border bg-gray-50 flex flex-col justify-center">
                  <div className="text-xs text-gray-500">Price / seat</div>
                  <div className="font-semibold">₹{currentTicketPrice.toFixed(2)}</div>
                </div>
              </div>

              <div className="mt-2 p-3 bg-white rounded shadow-sm border">
                <div className="text-sm text-gray-500">Total</div>
                <div className="text-lg font-bold">₹{currentTotal.toFixed(2)}</div>
              </div>

              <div className="flex gap-2">
                <button className="btn btn-primary">Create Booking</button>
                <button type="button" className="btn btn-outline" onClick={()=>setForm({ eventId:'', seats:1 })}>Reset</button>
              </div>
            </div>
          </form>
        )}

        {/* Toolbar / Filters */}
        <div className="p-4 bg-white rounded shadow col-span-2">
          <div className="flex items-center gap-3 mb-3">
            <input placeholder="Search by booking id" className="border p-2 rounded flex-1" value={searchId} onChange={e=>setSearchId(e.target.value)} />
            <button onClick={()=>{ setSearchId(''); fetchBookings(); }} className="btn btn-ghost">Reset / Refresh</button>
          </div>

          <div className="text-sm text-gray-600">Tip: Click a booking to view details or update payment status if you have permission.</div>
        </div>
      </div>

      {loading ? (
        <div className="p-4 bg-white rounded shadow">Loading bookings...</div>
      ) : (
        <div className="bookings-list space-y-3">
          {filtered.length === 0 && <div className="p-4 bg-white rounded shadow">No bookings found.</div>}

          {filtered.map(b => (
            <div key={b.id} className="booking-card p-4 bg-white rounded shadow flex gap-4 items-start">
              <div className="booking-thumb" style={{ backgroundImage: `url(/images/auditorium${((b.event_id||b.eventId||b.id) % 3) + 1}.jpg)` }} />

              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-bold booking-title">Booking #{b.id}</div>
                    <div className="text-sm text-gray-600">{getEventName(b.event_id || b.eventId)} • Seats: {b.seats}</div>
                  </div>

                  <div className="text-right">
                    <div className={paymentPillClass(b.payment_status || b.paymentStatus)}>{(b.payment_status || b.paymentStatus || 'Pending')}</div>
                    <div className="text-xs text-gray-400 mt-1">{b.createdAt || b.created_at || ''}</div>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <div className="text-sm text-gray-700">Amount: <strong>₹{b.amount}</strong></div>
                  <div className="flex items-center gap-2">
                    {canManagePayments ? (
                      <PaymentControls booking={b} onUpdate={updatePayment} />
                    ) : (
                      <button className="btn btn-outline btn-sm" onClick={()=>navigator.clipboard?.writeText(window.location.origin + '/events/' + (b.event_id || b.eventId))}>Share Event</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {user && (user.role === 'Admin' || user.role === 'Manager') && (
        <form onSubmit={async (e)=>{ e.preventDefault();
            const uId = e.target.userId?.value;
            const evId = e.target.mEventId?.value;
            const seats = e.target.mSeats?.value;
            if (!uId || !evId) return alert('Provide user and event');
            try{
              const r = await api.post('/bookings/create-for', { userId: uId, eventId: evId, seats: Number(seats || 1) });
              alert('Created booking #' + r.data.id);
              fetchBookings();
            }catch(err){ alert('Create failed: ' + (err?.message || 'error')); }
          }} className="p-4 bg-white rounded shadow mb-4">
          <h4 className="font-semibold">Manager tools</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
            <select name="userId" className="border p-2 rounded" id="mUserSelect" value={form.mUser || ''} onChange={(e)=>setForm(f=>({...f,mUser:e.target.value}))}>
              <option value="">Select user</option>
              {usersList.map(u => (<option key={u.id} value={u.id}>{u.name} ({u.email})</option>))}
            </select>
            <select name="mEventId" className="border p-2 rounded" value={form.eventId} onChange={e=>{ setForm(f=>({...f,eventId:e.target.value})); fetchAvailability(e.target.value); }}>
              <option value="">Select event</option>
              {events.map(ev => (<option key={ev.id} value={ev.id}>{ev.name} ({ev.date})</option>))}
            </select>
            <input name="mSeats" type="number" min="1" defaultValue={1} className="border p-2 rounded" />
          </div>
          <div className="mt-2"><button className="btn btn-primary">Create booking for user</button></div>
        </form>
      )}

      {/* Availability panel for selected event */}
      {availability && (
        <div className="p-4 bg-white rounded shadow mb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500">Event</div>
              <div className="font-semibold">{availability.event.name} • {availability.event.date} {availability.event.time}</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Capacity</div>
              <div className="font-bold">{availability.capacity}</div>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-4">
            <div className="flex-1">
              <div className="text-xs text-gray-500">Sold</div>
              <div className="text-lg font-semibold">{availability.seats_sold}</div>
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-500">Remaining</div>
              <div className="text-lg font-semibold">{availability.remaining}</div>
            </div>
            <div className="w-48">
              <div className="text-xs text-gray-500">Occupancy</div>
              <div className="w-full bg-gray-200 rounded h-3 overflow-hidden mt-1">
                <div className="bg-indigo-500 h-3" style={{ width: `${Math.round((availability.seats_sold / Math.max(1, availability.capacity)) * 100)}%` }} />
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

function PaymentControls({ booking, onUpdate }){
  const [status, setStatus] = useState(booking.payment_status || booking.paymentStatus || 'Pending');
  return (
    <div className="flex items-center gap-2">
      <select className="border p-1 rounded" value={status} onChange={e=>setStatus(e.target.value)}>
        <option>Pending</option>
        <option>Completed</option>
        <option>Refunded</option>
        <option>Cancelled</option>
      </select>
      <button className="btn btn-primary btn-sm" onClick={()=>onUpdate(booking.id, status)}>Save</button>
    </div>
  );
}
